/* eslint-disable @typescript-eslint/naming-convention */
/**
 * Define here models used
 * with the backend server
 **/

import { Organization } from './organization.models';
import { Permission } from './roles.models';
import { FreeRequestParams } from './shared.models';
import { TaxRule } from './tax-rules.models';

export type RequestParams = {
  itemPerPage?: number;
  page?: number;
  orderBy?: string;
  asc?: string;
  desc?: string;
  sort?: string;
  createdAt?: string;
  updatedAt?: string;
  details?: string;
};

export enum BackendErrorCode {
  monthlyInvoiceCertificationQuotaExceeded = 'MONTHLY_INVOICE_CERTIFICATION_QUOTA_EXCEEDED'
}

type CommonPagination<T> = {
  itemPerPage: number;
  orderBy?: Record<string, unknown>;
  itemOffset?: number;
  items: T[];
};

export type OffsetPagination<T> = CommonPagination<T> & {
  mode?: 'page';
  page: number;
  pageNumber: number;
  fullyItems: number;
  filters?: string[];
};

export type CursorPagination<T> = CommonPagination<T> & {
  mode: 'cursor';
  nextCursor: string | null;
  hasMore: boolean;
  filters?: Record<string, unknown>;
};

export type Pagination<T> = OffsetPagination<T> | CursorPagination<T>;

export type PaginationData = {
  mode?: 'page' | 'cursor';
  itemPerPage?: number;
  orderBy?: Record<string, unknown>;
  itemOffset?: number;
  page?: number;
  pageNumber?: number;
  fullyItems?: number;
  nextCursor?: string | null;
  hasMore?: boolean;
  filters?: string[] | Record<string, unknown>;
};

export type NotificationChannel = 'email' | 'whatsapp' | 'sms';

export type RelatedObject = {
  name: RelatedObjectName;
  identifier: string;
};

// export type RelatedObjectName =
//   | 'catalog'
//   | 'product_offering'
//   | 'product_offering_price'
//   | 'users'
//   | 'sales_point'
//   | 'websites'
//   | 'products'
//   | 'users'
//   | 'establishment';

export enum RelatedObjectName {
  attachment = 'attachment',
  control = 'control_result',
  campaign = 'campaign',
  asset = 'asset',
  establishment = 'establishment',
  salesPoint = 'sales_point'
}

export type RelatedLink = {
  type: 'document' | 'image' | 'video' | 'other';
  value: string;
};

export type SharedRequestParams = {
  useStateFormViolationHandler?: boolean;
  skipErrorInterceptor?: boolean;
};

export type ListRequestParams = SharedRequestParams & {
  createdAt?: string;
  updatedAt?: string;
  details?: string;
  itemPerPage?: number;
  page?: number;
  orderBy?: Record<string, unknown>;
};

export type PolyRelatedObject = {
  name: RelatedObjectName;
  identifiers: string[];
};

export enum UnitMeasureType {
  // Grandeurs fondamentales SI
  longueur = 'longueur',
  masse = 'masse',
  temps = 'temps',
  temperature = 'temperature',
  courantElectrique = 'courant_electrique',
  quantiteDeMatiere = 'quantite_de_matiere',
  intensiteLumineuse = 'intensite_lumineuse',

  // Grandeurs dérivées géométriques
  surface = 'surface',
  volume = 'volume',
  anglePlan = 'angle_plan',
  angleSolide = 'angle_solide',

  // Grandeurs mécaniques
  vitesse = 'vitesse',
  vitesseAngulaire = 'vitesse_angulaire',
  acceleration = 'acceleration',
  force = 'force',
  pression = 'pression',
  energie = 'energie',
  puissance = 'puissance',
  momentDeForce = 'moment_de_force',
  viscositeDynamique = 'viscosite_dynamique',
  viscositeCinematique = 'viscosite_cinematique',

  // Grandeurs électriques
  tensionElectrique = 'tension_electrique',
  resistanceElectrique = 'resistance_electrique',
  chargeElectrique = 'charge_electrique',
  capaciteElectrique = 'capacite_electrique',
  inductance = 'inductance',
  conductanceElectrique = 'conductance_electrique',
  puissanceApparente = 'puissance_apparente',
  puissanceReactive = 'puissance_reactive',

  // Grandeurs magnétiques
  fluxMagnetique = 'flux_magnetique',
  inductionMagnetique = 'induction_magnetique',
  champMagnetique = 'champ_magnetique',

  // Grandeurs lumineuses
  fluxLumineux = 'flux_lumineux',
  eclairementLumineux = 'eclairement_lumineux',

  // Grandeurs acoustiques
  niveauSonore = 'niveau_sonore',

  // Fréquence et radioactivité
  frequence = 'frequence',
  radioactivite = 'radioactivite',
  doseAbsorbee = 'dose_absorbee',
  doseEquivalente = 'dose_equivalente',

  // Données numériques et quantités
  donneesNumeriques = 'donnees_numeriques',
  quantite = 'quantite',
  proportion = 'proportion'
}

export enum TaxCategory {
  tva = 'tva'
}

export type SaleFormat = {
  code: string;
  label: string;
  conversionFactor: number;
  priceFormat: number;
};

export enum TaxKind {
  TSCE = 'TSCE',
  TVA = 'TVA',
  RRPSSI = 'RRPSSI',
  TOB = 'TOB',
  TSCT = 'TSCT',
  TSV = 'TSV',
  TEBA = 'TEBA',
  TSB = 'TSB',
  TSM = 'TSM',
  TST = 'TST',
  TSPP = 'TSPP',
  AIRSI = 'AIRSI',
  STAMP = 'STAMP',
  OTHER = 'OTHER'
}

export enum TaxBase {
  HT = 'ht',
  TTC = 'ttc'
}

export enum TaxScope {
  LINE = 'line',
  INVOICE = 'invoice'
}

export type Tax = {
  id: string;
  kind: TaxKind;
  label: string;
  code: string;
  base: TaxBase;
  scope: TaxScope;
  enabled: boolean;
  displayedOnInvoice?: boolean;
  accountId?: string;
  organizationId?: string;
  formula?: string;
  rules: TaxRule[];
  ruleCount: number;
  createdAt: string;
  updatedAt: string;
  // to remove
  name: string;
  type?: string;
  rate?: number;
  price?: {
    amount: number;
    currency: string;
  };
  description: string;
};

export type TaxCreationParams = {
  kind: TaxKind;
  label: string;
  code: string;
  base: TaxBase;
  scope: TaxScope;
  displayedOnInvoice?: boolean;
  accountId?: string;
  formula?: string;
  taxId?: string;
  included?: boolean;
};

export type TaxListParams = FreeRequestParams & {
  kind?: TaxKind;
  label?: string;
  code?: string;
  base?: TaxBase;
  scope?: TaxScope;
  displayedOnInvoice?: boolean;
  accountId?: string;
  formula?: string;
};

export type TaxProfile = {
  id: string;
  name: string;
  enabled: boolean;
  totalChildren?: number;
  organizations?: Organization;
  createdAt: string;
  updatedAt: string;
};

export type TaxProfileCreationParams = {
  name: string;
  enabled: boolean;
  organizationIds?: string[];
};

export type TaxProfileListParams = FreeRequestParams & {
  name?: TaxKind;
  enabled?: string;
};

export type UnitMeasure = {
  id: string;
  name: string;
  code: string;
  type: UnitMeasureType;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
};

export type CreateUnitMeasureParams = {
  name: string;
  code: string;
};
export type Product = {
  id: string;
  designation: string;
  category: string;
  reference: string;
  unitPrice: Money;
  description?: string;
  organizationId: string;
  baseUnit: UnitMeasure;
  saleFormats: SaleFormat[];
  taxes?: Tax[];
  name?: string;
  code?: string;
  price?: Money;
  unitMeasure?: UnitMeasure;
  createdAt: string;
  updatedAt: string;
  discountedPrice?: Money;
};

export type CreateProductParams = {
  reference?: string;
  designation: string;
  unitPrice: Money | number;
  description?: string;
  organizationId?: string;
  baseUnitId: UnitMeasure | null | string;
  taxes?: Tax[];
  saleFormats?: SaleFormat[];
};

export type Link = {
  rel: 'self' | 'web';
  methods: Array<'GET' | 'PUT' | 'DELETE'>;
  url: string;
};

export type ChartsFilters = {
  from?: number;
  to?: number;
  panelId?: string;
  accessTokenId?: string;
  channel?: string;
};

export type ChartName =
  | 'engagement'
  | 'featured_products'
  | 'social_performance';

export type Point = {
  longitude: number;
  latitude: number;
  srid?: number;
};

export type Address = {
  country: string;
  city: string;
  countryCode?: string;
  district?: string;
  neighborhood?: string;
  uniqueIdentifier?: string;
  location?: string;
  longitude?: string;
  latitude?: string;
  section?: string;
  parcel?: string;
  lot?: string;
  ilot?: string;
  zipcode?: string;
};

export type UserPreferences = {
  'security.authentication.double_factor': boolean;
  'security.authentication.watchdog': boolean;

  //NotificationSettings
  'notification.new_registration': boolean;
  'notification.new_video_publication': boolean;
  'notification.form_submission': boolean;
  'notification.contentSharing': boolean;
  //account completed
  'account.status_completed': boolean;
  'onboarding.console_completed': boolean;
};

export type Money = {
  amount: number;
  currency: string;
};

export enum DocumentType {
  INVOICE = 'invoice',
  RECEIPT = 'receipt'
}

export enum DashboardSourceType {
  SUPERSET = 'superset',
  INTERNAL = 'internal',
  EXTERNAL = 'external'
}

export type DashboardSource = {
  type: DashboardSourceType;
  id: string;
  name: string;
  url: string;
};

export type SuperSetTokenCreationParams = {
  resources: { id: string; type: 'dashboard' | 'embedded' }[];
};

export type Dashboard = {
  id: string;
  name: string;
  source: DashboardSource;
  description?: string;
  permissions?: Permission[];
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  //computed properties
  sourceEmbeddedUrl?: string;
};

export type SuperSetDashboard = {
  id: string;
  title: string;
  thumbnailUrl: string;
  url: string;
  order?: number;
  visibleInApp?: boolean;
};

export type SuperSetDashboardParams = {
  startDateTime?: string;
  endDateTime?: string;
};

export type DashboardCreationParams = {
  name: string;
  source: {
    id: string | number;
    name: string;
    url: string;
    type: DashboardSourceType;
  };
  enabled?: boolean;
  description?: string;
  permissions?: string[];
};

export type DashboardListParams = FreeRequestParams & {
  name?: string;
  sourceType?: DashboardSourceType;
  permission?: string;
};

export type SupersetQuery = {
  filters: {
    col: string;
    op: '>=' | '<=' | '=' | '!=' | '>' | '<';
    val: string;
  }[];
}[];
